/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import {
    Button,
    IconButton,
    MenuItem,
    Select,
    Checkbox,
    Tooltip,
    Box,
    Typography,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import PublicIcon from "@mui/icons-material/Public";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import WebAssetOutlinedIcon from "@mui/icons-material/WebAssetOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import SearchBar from "../../components/SearchBar";

import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";

const MediaLibrary = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedAssets, setSelectedAssets] = useState<number[]>([]);
    const [filterFolder, setFilterFolder] = useState("all");
    const [filterTag, setFilterTag] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [showArchived, setShowArchived] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<any>(null);

    const handlePreview = (file: any) => {
        setSelectedImage(file);
        setPreviewOpen(true);
    };

    const mediaFiles = [
        {
            id: 1,
            name: "summer-sale-banner.jpg",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: ["Homepage hero", "Email / Campaign"],
            ownership: { uploadedBy: "Admin", source: "Campaign" },
            status: "In use",
            folder: "Campaigns",
            tags: ["Banner", "Summer"],
            health: { oversized: true, lowRes: false, wrongAspect: false },
            aiRecommendation: "Best suited for: Homepage hero, Campaign banner. Suggest better crop for mobile."
        },
        {
            id: 2,
            name: "electronics-category.jpg",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: ["Category banner"],
            ownership: { uploadedBy: "System", source: "Category" },
            status: "In use",
            folder: "Categories",
            tags: ["Thumbnail", "Electronics"],
            health: { oversized: false, lowRes: false, wrongAspect: false },
            aiRecommendation: "Best suited for: Category banner. Aspect ratio is perfect for sidebar."
        },
        {
            id: 3,
            name: "man-blue-shirt.jpg",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: ["Product gallery"],
            ownership: { uploadedBy: "Vendor", source: "Product" },
            status: "In use",
            folder: "Vendors",
            tags: ["Square", "Blue"],
            health: { oversized: false, lowRes: true, wrongAspect: false },
            aiRecommendation: "Best suited for: Product gallery. Low resolution detected, consider a higher res upload."
        },
        {
            id: 4,
            name: "boutique-logo.png",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: ["Homepage hero", "CMS"],
            ownership: { uploadedBy: "Admin", source: "CMS" },
            status: "In use",
            folder: "Marketing",
            tags: ["Logo", "Transparent"],
            health: { oversized: false, lowRes: false, wrongAspect: false },
            aiRecommendation: "Best suited for: Navbar, Footer logo."
        },
        {
            id: 5,
            name: "winter-collection-draft.jpg",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: [],
            ownership: { uploadedBy: "Admin", source: "Campaign" },
            status: "Draft",
            folder: "Campaigns",
            tags: ["Portrait", "Winter"],
            health: { oversized: false, lowRes: false, wrongAspect: true },
            aiRecommendation: "Best suited for: Collection preview. Aspect ratio might need adjustment for mobile."
        },
        {
            id: 6,
            name: "old-banner-deprecated.jpg",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: [],
            ownership: { uploadedBy: "System", source: "CMS" },
            status: "Deprecated",
            folder: "Marketing",
            tags: ["Banner", "Archived"],
            health: { oversized: false, lowRes: false, wrongAspect: false },
            aiRecommendation: "Consider replacement with high-performing 2026 banners."
        },
        {
            id: 7,
            name: "archived-product-shot.jpg",
            type: "image",
            src: "/static/images/placeholder.png",
            usage: [],
            ownership: { uploadedBy: "Vendor", source: "Product" },
            status: "Archived",
            folder: "Vendors",
            tags: ["Square", "Archived"],
            health: { oversized: false, lowRes: false, wrongAspect: false },
            aiRecommendation: "Asset archived due to product discontinuation."
        },
    ];
    const filteredMedia = mediaFiles.filter((m) => {
        const isArchived = m.status === "Archived" || m.status === "Deprecated";
        const matchesView = showArchived ? isArchived : !isArchived;

        const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
            m.usage.some(u => u.toLowerCase().includes(searchTerm.toLowerCase())) ||
            m.folder.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFolder = filterFolder === "all" || m.folder === filterFolder;
        const matchesTag = filterTag === "all" || m.tags.includes(filterTag);
        const matchesType = filterType === "all" || 
            (filterType === "Photos" && m.type === "image") || 
            (filterType === "Videos" && m.type === "video") || 
            (filterType === "Reels" && m.type === "reel");

        return matchesView && matchesSearch && matchesFolder && matchesTag && matchesType;
    });

    const toggleSelectAsset = (id: number) => {
        setSelectedAssets(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const toggleSelectAll = (checked: boolean) => {
        setSelectedAssets(checked ? filteredMedia.map(m => m.id) : []);
    };

    return (
        <>
            <div className="main_title">
                <h1>Media Library</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Media Library
                </p>
            </div>

            <div className="stats_boxes gap_m">
                <a className="cards">
                    <div className="data_top">
                        <h2>
                            {mediaFiles.length}
                            <p>Total Files</p>
                        </h2>
                        <figure className="blue">
                            <InsertDriveFileOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>

                <a className="cards">
                    <div className="data_top">
                        <h2>
                            {mediaFiles.filter(m => m.type === 'image').length}
                            <p>Images</p>
                        </h2>
                        <figure className="success">
                            <ImageOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>

                <a className="cards">
                    <div className="data_top">
                        <h2>
                            {mediaFiles.filter(m => m.type === 'video').length}
                            <p>Videos</p>
                        </h2>
                        <figure className="purple">
                            <VideoLibraryOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>

                <a className="cards">
                    <div className="data_top">
                        <h2>
                            10.8 MB
                            <p>Storage Used</p>
                        </h2>
                        <figure className="warning">
                            <StorageOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>
            </div>

            <div className="cards mt_20">
                {/* Bulk Action Bar */}
                {selectedAssets.length > 0 && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 20px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        mb: 2,
                        border: '1px solid #e2e8f0',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                    }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {selectedAssets.length} Selected
                        </Typography>
                        <Button variant="outlined" size="small" startIcon={<LocalOfferOutlinedIcon />}>Assign tags</Button>
                        <Button variant="outlined" size="small" startIcon={<FolderOutlinedIcon />}>Move folder</Button>
                        <Button variant="outlined" size="small" startIcon={<ArchiveOutlinedIcon />} color="warning">Archive</Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button onClick={() => setSelectedAssets([])} sx={{ textTransform: 'none' }}>Cancel</Button>
                    </Box>
                )}

                <div className="table_header">
                    <div className="left_s">
                        <SearchBar
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={setSearchTerm}
                            placeholder="Search name, tags, or usage..."
                        />
                        <div className="form" style={{ display: 'flex', gap: '10px' }}>
                            <Select
                                value={filterFolder}
                                onChange={(e) => setFilterFolder(e.target.value)}
                                displayEmpty
                                size="small"
                                sx={{ minWidth: 140 }}
                            >
                                <MenuItem value="all">All Folders</MenuItem>
                                <MenuItem value="Marketing">Marketing</MenuItem>
                                <MenuItem value="Categories">Categories</MenuItem>
                                <MenuItem value="Vendors">Vendors</MenuItem>
                                <MenuItem value="Campaigns">Campaigns</MenuItem>
                            </Select>
                            <Select
                                value={filterTag}
                                onChange={(e) => setFilterTag(e.target.value)}
                                displayEmpty
                                size="small"
                                sx={{ minWidth: 140 }}
                            >
                                <MenuItem value="all">All Tags</MenuItem>
                                <MenuItem value="Banner">Banner</MenuItem>
                                <MenuItem value="Thumbnail">Thumbnail</MenuItem>
                                <MenuItem value="Square">Square</MenuItem>
                                <MenuItem value="Portrait">Portrait</MenuItem>
                            </Select>
                            <Select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                displayEmpty
                                size="small"
                                sx={{ minWidth: 140 }}
                            >
                                <MenuItem value="all">All Types</MenuItem>
                                <MenuItem value="Photos">Photos</MenuItem>
                                <MenuItem value="Videos">Videos</MenuItem>
                                <MenuItem value="Reels">Reels</MenuItem>
                            </Select>
                        </div>
                    </div>
                    <div className="right_s">
                        <Button
                            variant={showArchived ? "contained" : "outlined"}
                            color={showArchived ? "primary" : "inherit"}
                            startIcon={showArchived ? <PublicIcon /> : <ArchiveOutlinedIcon />}
                            onClick={() => setShowArchived(!showArchived)}
                            sx={{ textTransform: 'none', borderRadius: '6px' }}
                        >
                            {showArchived ? "Show Active Assets" : "View Archived"}
                        </Button>
                    </div>
                </div>

                <Box sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Checkbox
                            indeterminate={selectedAssets.length > 0 && selectedAssets.length < filteredMedia.length}
                            checked={filteredMedia.length > 0 && selectedAssets.length === filteredMedia.length}
                            onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                        <Typography variant="caption">Select All</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {filteredMedia.map((file) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                                <div className={`media_card ${selectedAssets.includes(file.id) ? 'selected' : ''}`} style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div className="media_thumb" style={{ height: '160px', overflow: 'hidden', borderRadius: '8px 8px 0 0', position: 'relative' }}>
                                        <Checkbox
                                            sx={{ position: 'absolute', top: 4, left: 4, zIndex: 5, color: '#fff', '&.Mui-checked': { color: '#3b82f6' } }}
                                            checked={selectedAssets.includes(file.id)}
                                            onChange={() => toggleSelectAsset(file.id)}
                                        />

                                        <div className="health_signals" style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: '4px', zIndex: 5 }}>
                                            {file.health.oversized && (
                                                <Tooltip title="Oversized File">
                                                    <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 18, background: '#fff', borderRadius: '50%' }} />
                                                </Tooltip>
                                            )}
                                            {file.health.lowRes && (
                                                <Tooltip title="Low Resolution">
                                                    <InfoOutlinedIcon sx={{ color: '#f59e0b', fontSize: 18, background: '#fff', borderRadius: '50%' }} />
                                                </Tooltip>
                                            )}
                                            {file.health.wrongAspect && (
                                                <Tooltip title="Wrong Aspect Ratio for typical usage">
                                                    <AspectRatioIcon sx={{ color: '#ef4444', fontSize: 18, background: '#fff', borderRadius: '50%' }} />
                                                </Tooltip>
                                            )}
                                        </div>

                                        <img src={file.src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

                                        <div className="media_overlay">
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Archive">
                                                    <IconButton className="delete_btn" sx={{ background: 'rgba(255,255,255,0.9)' }}>
                                                        <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {file.usage.length === 0 && (
                                                    <Tooltip title="Force Delete (Unused)">
                                                        <IconButton className="delete_btn" color="error" sx={{ background: 'rgba(255,255,255,0.9)' }}>
                                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </div>
                                    </div>

                                    <Box sx={{ p: 1.5, flexGrow: 1, border: '1px solid #f1f5f9', borderTop: 0, borderRadius: '0 0 8px 8px' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <p className="media_name" style={{ margin: 0, fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%', cursor: 'pointer' }} onClick={() => handlePreview(file)}>
                                                {file.name}
                                            </p>
                                            <Chip
                                                label={file.status}
                                                size="small"
                                                className={`badge1 ${file.status.toLowerCase().replace(' ', '_')}`}
                                                sx={{ fontSize: '10px', height: '18px' }}
                                            />
                                        </Box>

                                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                                            {file.tags.map(tag => (
                                                <Chip key={tag} label={tag} variant="outlined" size="small" sx={{ fontSize: '10px', height: '18px' }} />
                                            ))}
                                        </Box>

                                        <Box mt={1}>
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                <b>Folder:</b> {file.folder}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                <b>Used in:</b> {file.usage.length > 0 ? file.usage.join(', ') : 'None'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                <b>Uploaded by:</b> {file.ownership.uploadedBy}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </div>

            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                    <Box>
                        <Typography variant="h6">{selectedImage?.name}</Typography>
                        <Typography variant="caption" color="textSecondary">{selectedImage?.folder} / {selectedImage?.type}</Typography>
                    </Box>
                    <IconButton onClick={() => setPreviewOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Grid container sx={{ height: '70vh' }}>
                        <Grid item xs={12} md={8} sx={{ background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                            {selectedImage && (
                                <img
                                    src={selectedImage.src}
                                    alt={selectedImage.name}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '4px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ borderLeft: '1px solid #e2e8f0', p: 3, overflowY: 'auto' }}>
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoOutlinedIcon fontSize="small" color="primary" /> Asset Details
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">UPLOADED BY</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PublicIcon sx={{ fontSize: 14 }} /> {selectedImage?.ownership.uploadedBy}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">SOURCE</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {selectedImage?.ownership.source === 'Product' && <ShoppingBagOutlinedIcon sx={{ fontSize: 14 }} />}
                                            {selectedImage?.ownership.source === 'Category' && <FolderOutlinedIcon sx={{ fontSize: 14 }} />}
                                            {selectedImage?.ownership.source === 'CMS' && <WebAssetOutlinedIcon sx={{ fontSize: 14 }} />}
                                            {selectedImage?.ownership.source === 'Campaign' && <CampaignOutlinedIcon sx={{ fontSize: 14 }} />}
                                            {selectedImage?.ownership.source}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">STATUS</Typography>
                                        <Box mt={0.5}>
                                            <Chip label={selectedImage?.status} size="small" className={`badge1 ${selectedImage?.status.toLowerCase().replace(' ', '_')}`} />
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">USAGE COUNT</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedImage?.usage.length} Places</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Usage Locations</Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {selectedImage?.usage.map((u: string) => (
                                        <Chip key={u} label={u} size="small" variant="outlined" />
                                    ))}
                                    {selectedImage?.usage.length === 0 && <Typography variant="body2" color="textSecondary">Not in use</Typography>}
                                </Box>
                            </Box>

                            <Box sx={{ mb: 4, p: 2, background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#0369a1' }}>
                                    <AutoFixHighOutlinedIcon fontSize="small" /> AI Recommendation
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#0c4a6e', lineHeight: 1.5, fontSize: '13px' }}>
                                    {selectedImage?.aiRecommendation}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
                                <Button fullWidth variant="contained" sx={{ background: '#3b82f6', textTransform: 'none' }}>Replace File</Button>
                                <Button fullWidth variant="outlined" color="error" sx={{ textTransform: 'none' }}>
                                    {selectedImage?.usage.length > 0 ? "Archive" : "Force Delete"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MediaLibrary;
